import React, { forwardRef } from "react";
import { motion } from "framer-motion";
import { cva } from "class-variance-authority";
import { cn } from "../../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 font-medium transition-all duration-200 disabled:opacity-50  disabled:cursor-not-allowed disabled:pointer-events-none ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        default: "bg-red-300 text-gray-100 hover:bg-red-400 active:bg-red-600",
        primary: "bg-red-300 text-gray-100 hover:bg-red-400 active:bg-red-600",
        secondary:
          "border border-neutral-green-100 text-neutral-green-700 hover:shadow-md active:bg-gray-100",
        outline:
          "border-1 border-red-400 text-red-400 hover:bg-red-50 active:bg-red-100",
        ghost:
          "bg-transparent text-neutral-green-400 hover:text-neutral-green-500 hover:bg-gray-100 active:bg-gray-200",
        danger: "bg-red-500 text-gray-100 hover:bg-red-600 active:bg-red-700",
        success:
          "bg-green-600 text-gray-100 hover:bg-green-700 active:bg-green-800",
        shopify: "bg-[#95C046] text-gray-100 hover:bg-[#74943a]",
        link: "bg-transparent text-red-500 hover:text-red-600 hover:underline p-0",
        gradient:
          "bg-gradient-to-r from-red-600 to-orange-600 text-gray-100 hover:from-red-700 hover:to-orange-700",
        icon: "bg-transparent hover:bg-gray-100 active:bg-gray-200 text-gray-600 p-2",
        nav: "bg-transparent text-neutral-green-500 hover:text-red-500 font-medium p-0",
        navActive:
          "bg-transparent text-red-500 font-semibold hover:text-red-500 p-0",
        menuItem:
          "text-gray-900 hover:bg-gray-200 text-left justify-start font-normal w-full rounded-none px-4 py-2 text-sm",
        favorite:
          " hover:bg-red-400 hover:text-white text-neutral-green-500 group/button",
        custom: "", // No default styles, allows full customization via className
      },
      size: {
        none: "text-sm p-0",
        xs: "px-1.5 py-1 text-xs",
        xs2: "px-3 py-2 text-xs",
        sm: "px-3 py-2 text-sm",
        md: "px-4 py-2.5 text-base ",
        long: "px-14 py-2.5 text-base",
        lg: "px-6 py-2.5 text-lg",
        xl: "px-8 py-4 text-xl",
        icon: "p-2.5",
        iconMd: "p-2",
        iconSm: "p-1.5",
      },
      rounded: {
        none: "rounded-none",
        sm: "rounded-sm",
        md: "rounded-md",
        lg: "rounded-lg",
        xl: "rounded-xl",
        "2xl": "rounded-2xl",
        "3xl": "rounded-3xl",
        full: "rounded-full",
      },
      fullWidth: {
        true: "w-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
      rounded: "md",
      fullWidth: false,
    },
  }
);

const LoadingSpinner = () => (
  <svg
    className="animate-spin h-4 w-4"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const Button = forwardRef(
  (
    {
      className,
      variant,
      size,
      rounded,
      fullWidth,
      as = "button",
      type = "button",
      withMotion = false,
      loading = false,
      disabled = false,
      leftIcon,
      rightIcon,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = withMotion ? motion[as] || motion.button : as;

    const motionProps = withMotion
      ? {
        whileHover: { scale: disabled || loading ? 1 : 1.02 },
        whileTap: { scale: disabled || loading ? 1 : 0.98 },
        transition: { duration: 0.1 },
      }
      : {};

    return (
      <Comp
        ref={ref}
        type={type}
        className={cn(
          buttonVariants({ variant, size, rounded, fullWidth, className })
        )}
        disabled={disabled || loading}
        {...motionProps}
        {...props}
      >
        {loading && <LoadingSpinner />}
        {!loading && leftIcon && (
          <span className="flex-shrink-0">{leftIcon}</span>
        )}
        {children}
        {!loading && rightIcon && (
          <span className="flex-shrink-0">{rightIcon}</span>
        )}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
export default Button;
